import React, { useMemo, useState, useRef } from 'react';
import { Lock, Trash2 } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import CollapseExpandButton from './ui/collapse-expand-button';
import PartLookupInput from './ui/part-lookup-input';
import VehicleSectionPicker from './VehicleSectionPicker';
import { QUOTE_CATEGORIES } from '../pages/overlays/quoteCategories';
import { useQuoteModel } from '../context/QuoteModelContext';
import { partsPrice, clampQuantity, MAX_PART_QUANTITY } from '../lib/damageLines';

const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

// A line's count, as the field should show it. The projection only writes a
// quantity onto a line that carries more than one, so an absent one is one.
const lineQuantity = (line) => clampQuantity(line?.quantity);

// Show a number for editing without a stray leading zero. Zero/blank render empty
// so the placeholder shows through and typing over them can't produce "05".
const numericFieldValue = (v) => (v === 0 || v == null || v === '' ? '' : v);

// Strip leading zeros from a typed numeric string ("05" -> "5"; "0.5" is kept).
const stripLeadingZeros = (s) => (typeof s === 'string' ? s.replace(/^0+(?=\d)/, '') : s);

// Sanitize a numeric field's keystroke: strip the leading zero from the field
// itself (React won't re-sync the DOM when the parsed number is unchanged, e.g.
// prepending "0" to "5"), then hand the cleaned string to the caller.
const handleNumericChange = (e, apply) => {
  const cleaned = stripLeadingZeros(e.target.value);
  if (cleaned !== e.target.value) e.target.value = cleaned;
  apply(cleaned);
};

// Categories whose lines are damage parts (appear in the damage assessment).
// Adding to one of these creates a damage part; the rest stay quote-only extras.
const DAMAGE_CATEGORIES = new Set(['Remove & Refit', 'Panel Beating', 'Painting', 'Parts']);

// Sections mirror the damage assessment zones so the section selector is consistent.
const ZONE_OPTIONS = [
  { id: 'front', label: 'Front' },
  { id: 'front-right', label: 'Front Right' },
  { id: 'right', label: 'Right' },
  { id: 'rear-right', label: 'Rear Right' },
  { id: 'rear', label: 'Rear' },
  { id: 'rear-left', label: 'Rear Left' },
  { id: 'left', label: 'Left' },
  { id: 'front-left', label: 'Front Left' },
  { id: 'roof', label: 'Roof' },
  { id: 'underside', label: 'Underside' },
  { id: 'other', label: 'Other' },
];
const zoneLabel = (id) => ZONE_OPTIONS.find((z) => z.id === id)?.label || 'Other';

/**
 * The single-quote editor. A view over the shared quote model: every line is
 * either damage-managed (projected from a damage part — edits map back onto the
 * part) or a quote-only "extra". Both live in one store, so edits here show up in
 * the damage assessment and vice versa.
 */
const QuoteEditor = ({ canUpdateLinePrice = true }) => {
  const {
    lines, totals, notes, updateNotes, labourRate, insurerConfigId, partMarkups,
    updateDerivedLine, deleteDerivedLine,
    addExtraLine, updateExtraLine, removeExtraLine, overrideAllowanceLine,
    addDamagePart, movePartToZone, toggleLineBlend,
  } = useQuoteModel();

  const [expanded, setExpanded] = useState({});
  // Line id whose section selector is currently open (ghost-button edit).
  const [sectionEditId, setSectionEditId] = useState(null);
  // A matched line's id is captured here (synchronously, in onPartSelected) so the
  // onChange that fires immediately after can read it without a state race.
  const pendingLineRef = useRef({});

  const canDerive = Number(labourRate) > 0;

  // Parts are quoted as cost + markup, and the insurer sets the markup. IAG rates
  // it per part type (OEM 10%, aftermarket/exchange/used 20%). Ando and FMG charge
  // the same 15% regardless of source, but still publish "New" and "Used" types —
  // the price doesn't change, but the assessor can record what was fitted, which
  // Panel Quote export carries through as the real source instead of
  // "Unknown/Mixed". An insurer with only one type (nothing to choose between)
  // shows no Type column at all; the single rate just applies.
  const markupTypes = partMarkups || [];
  const showTypeColumn = markupTypes.length > 1;
  const canEditMarkup = canUpdateLinePrice || markupTypes.length === 0;

  const linesByCategory = useMemo(() => {
    const map = {};
    QUOTE_CATEGORIES.forEach((c) => { map[c.id] = []; });
    (lines || []).forEach((l) => {
      if (!map[l.category]) map[l.category] = [];
      map[l.category].push(l);
    });
    return map;
  }, [lines]);

  const categoryTotals = useMemo(() => {
    const t = {};
    Object.keys(linesByCategory).forEach((cat) => {
      t[cat] = linesByCategory[cat].reduce((s, l) => s + (Number(l.amount) || 0), 0);
    });
    return t;
  }, [linesByCategory]);

  const toggleCategory = (id) => setExpanded((p) => ({ ...p, [id]: !p[id] }));

  const allowanceTitle = (canOverride) => (canOverride
    ? 'Insurer allowance — calculated automatically, and recalculated as the quote changes. Type over it to use your own figure instead.'
    : 'Insurer allowance — calculated automatically. This insurer does not allow editing quote line item prices.');

  const editLine = (line, category, field, value) => {
    // Editing a calculated allowance's amount replaces it with the assessor's
    // figure rather than editing a number that is about to be recalculated.
    if (line.computed && line.allowanceId && field === 'amount') {
      overrideAllowanceLine(line, parseFloat(value) || 0);
      return;
    }
    if (line.damageManaged) {
      updateDerivedLine(line, field, value);
      return;
    }
    // Quote-only parts lines carry their own cost/markup and reprice on edit —
    // damage-derived ones reproject from the part (see inversePatchForLineEdit).
    if (field === 'partsCost' || field === 'markupCode' || field === 'markupPercent') {
      const num = (v) => (v === '' || v == null ? null : parseFloat(v) || 0);
      const patch = {};
      if (field === 'partsCost') patch.partsCost = num(value);
      if (field === 'markupPercent') patch.markupPercent = num(value) ?? 0;
      if (field === 'markupCode') {
        patch.markupCode = value || null;
        patch.markupPercent = markupTypes.find((m) => m.code === value)?.markupPercent ?? 0;
      }
      const cost = 'partsCost' in patch ? patch.partsCost : (line.partsCost ?? null);
      const percent = 'markupPercent' in patch ? patch.markupPercent : (line.markupPercent ?? 0);
      updateExtraLine(line.id, { ...patch, amount: partsPrice(cost, percent) });
      return;
    }
    if (field === 'hours') {
      const hours = value === '' ? null : parseFloat(value) || 0;
      const patch = { hours };
      if (category.hasHours && canDerive) patch.amount = round2((hours || 0) * labourRate);
      updateExtraLine(line.id, patch);
    } else if (field === 'amount') {
      updateExtraLine(line.id, { amount: parseFloat(value) || 0 });
    } else {
      updateExtraLine(line.id, { [field]: value });
    }
  };

  const deleteLine = (line) => {
    if (line.damageManaged) deleteDerivedLine(line);
    else removeExtraLine(line.id);
  };

  // Called when the lookup commits a value (Enter or dropdown selection).
  // Damage-category lines become damage parts (so they appear in the damage
  // assessment, in their default section); everything else is a quote-only extra.
  const submitAdd = (category, rawName) => {
    const name = (rawName || '').trim();
    const lineAllowanceId = pendingLineRef.current[category.id] || null;
    pendingLineRef.current[category.id] = null;
    if (!name) return;

    if (DAMAGE_CATEGORIES.has(category.id)) {
      addDamagePart({ category: category.id, name, lineAllowanceId });
    } else {
      addExtraLine({
        category: category.id,
        description: name,
        part: name,
        lineAllowanceId,
        hours: category.hasHours ? 0 : null,
        amount: 0,
      });
    }
  };

  const renderSectionCell = (line) => {
    // Section applies to damage-managed lines (they are damage parts). Extras and
    // computed allowance lines have no section.
    if (!line.damageManaged || line.computed) {
      return <div className="w-28 flex-shrink-0 text-center text-xs text-muted-foreground">—</div>;
    }
    return (
      <div className="w-28 flex-shrink-0 flex justify-center">
        <Popover open={sectionEditId === line.id} onOpenChange={(o) => setSectionEditId(o ? line.id : null)}>
          <PopoverTrigger asChild>
            <button
              className="h-8 px-2 inline-flex items-center justify-center rounded-md text-xs text-muted-foreground border border-transparent hover:bg-accent hover:text-foreground hover:border-input"
              title="Change section"
            >
              {zoneLabel(line.zone)}
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-3" align="center">
            <div className="text-xs font-medium text-muted-foreground mb-2">Section</div>
            <VehicleSectionPicker
              value={line.zone || 'other'}
              onSelect={(z) => { movePartToZone(line.groupId, z); setSectionEditId(null); }}
            />
          </PopoverContent>
        </Popover>
      </div>
    );
  };

  // Cost / markup type / markup % / price. The price is the only cell that is
  // never editable: it is what cost and markup produce, so hand-editing it would
  // just hide a wrong cost. That is the whole point of the parts columns.
  //
  // An insurer allowance can also be categorised as Parts (it is calculated, not
  // bought), so it takes the same columns with nothing to fill in but its amount.
  const renderPartsCells = (line, category, computed, canOverrideAllowance) => {
    const cost = line.partsCost ?? null;
    const percent = line.markupPercent ?? 0;
    const markupTitle = !canEditMarkup
      ? 'Set by the part type — this insurer fixes the markup per type.'
      : undefined;
    const priceCell = (
      <div className="relative w-24 flex-shrink-0">
        <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
        <Input
          type="number"
          readOnly
          tabIndex={-1}
          value={numericFieldValue(round2(line.amount))}
          placeholder="0.00"
          title={computed ? allowanceTitle(false) : `Cost + ${percent}% markup — calculated automatically.`}
          className="w-full pl-5 text-right bg-muted text-muted-foreground cursor-not-allowed"
        />
      </div>
    );

    if (computed) {
      const blank = (w) => <div className={`${w} flex-shrink-0 text-center text-xs text-muted-foreground`}>—</div>;
      return (
        <>
          {showTypeColumn && blank('w-28')}
          {blank('w-24')}
          {blank('w-16')}
          {canOverrideAllowance ? (
            <div className="relative w-24 flex-shrink-0">
              <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
              <Input
                type="number"
                placeholder="0.00"
                value={numericFieldValue(round2(line.amount))}
                onChange={(e) => handleNumericChange(e, (v) => editLine(line, category, 'amount', v))}
                title={allowanceTitle(true)}
                className="w-full pl-5 text-right"
              />
            </div>
          ) : priceCell}
        </>
      );
    }

    return (
      <>
        {showTypeColumn && (
          <div className="w-28 flex-shrink-0">
            <Select
              value={line.markupCode || ''}
              onValueChange={(v) => editLine(line, category, 'markupCode', v)}
            >
              <SelectTrigger className="h-10 px-2 text-sm" title="Part type — sets the markup">
                <SelectValue placeholder="Type">
                  {markupTypes.find((m) => m.code === line.markupCode)?.label}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {markupTypes.map((m) => (
                  <SelectItem key={m.code} value={m.code}>
                    {m.label}
                    <span className="ml-2 text-muted-foreground">{m.markupPercent}%</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        {/* Cost — what the shop pays */}
        <div className="relative w-24 flex-shrink-0">
          <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
          <Input
            type="number"
            min="0"
            placeholder="0.00"
            value={numericFieldValue(cost)}
            onChange={(e) => handleNumericChange(e, (v) => editLine(line, category, 'partsCost', v))}
            title="Cost price — what the part costs the shop"
            className="w-full pl-5 text-right"
          />
        </div>
        {/* Markup — driven by the type when the insurer fixes one */}
        <div className="relative w-16 flex-shrink-0">
          <Input
            type="number"
            min="0"
            placeholder="0"
            value={numericFieldValue(percent)}
            onChange={(e) => handleNumericChange(e, (v) => editLine(line, category, 'markupPercent', v))}
            readOnly={!canEditMarkup}
            tabIndex={!canEditMarkup ? -1 : undefined}
            title={markupTitle}
            className={`w-full pr-5 text-right ${!canEditMarkup ? 'bg-muted text-muted-foreground cursor-not-allowed' : ''}`}
          />
          <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
        </div>
        {/* Price — cost + markup, always derived */}
        {priceCell}
      </>
    );
  };

  const renderLineRow = (line, category) => {
    const computed = line.computed; // insurer allowance — auto-calculated
    // A calculated allowance is a default, not a rule: the amount stays editable
    // (unless the insurer forbids price edits outright), and editing it swaps in
    // an ordinary line that no longer recalculates. Everything else about the
    // line stays locked — its description and category come from the insurer.
    const canOverrideAllowance = Boolean(computed && line.allowanceId && canUpdateLinePrice);
    const isOverriddenAllowance = line.source === 'allowance-override';
    const isPartsLine = category.id === 'Parts';
    const amountLocked = computed
      ? !canOverrideAllowance
      : (category.hasHours && canDerive && !canUpdateLinePrice);
    const isBlend = line.blend || line.role === 'blend';
    return (
      // pl matches the damage assessment line items' drag-handle offset so the two
      // lists line up horizontally down the page.
      <div key={line.id} className="flex items-center gap-2 py-1 pl-7.5 group/item">
        {/* Description — fills the row; column headers align from here rightward */}
        <div className="flex-1 min-w-0 flex items-center gap-2">
          <Input
            type="text"
            value={line.description || ''}
            placeholder="Description"
            onChange={(e) => editLine(line, category, 'description', e.target.value)}
            readOnly={computed}
            className="flex-1"
          />
          {computed && (
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground border rounded px-1 py-0.5 whitespace-nowrap" title="Insurer allowance — calculated automatically">
              auto
            </span>
          )}
          {isOverriddenAllowance && (
            <span
              className="text-[10px] uppercase tracking-wide text-muted-foreground border rounded px-1 py-0.5 whitespace-nowrap"
              title="Insurer allowance, overridden — remove this line to go back to the calculated amount."
            >
              edited
            </span>
          )}
        </div>
        {renderSectionCell(line)}
        <div className="flex items-center justify-center w-12 flex-shrink-0">
          <Checkbox
            checked={line.report || false}
            disabled={computed}
            onCheckedChange={(checked) => editLine(line, category, 'report', checked)}
          />
        </div>
        {category.id === 'Painting' && (
          <div className="flex items-center justify-center w-12 flex-shrink-0">
            <Checkbox
              checked={isBlend}
              disabled={computed || !line.damageManaged}
              onCheckedChange={(checked) => toggleLineBlend(line, !!checked)}
              title="Blend (priced at the blend rate rather than a full refinish)"
            />
          </div>
        )}
        {/* Quantity. The insurer guides carry ONE row for a fitting a car wears
            several of — one "Bumper Mould" line whether the bumper has one
            moulding or four — so the count is the only thing that bills the
            rest. Everything to the right of this cell is already multiplied by
            it. Only damage-managed lines carry a count; a quote-only extra is
            already written for what it is. */}
        <div className="w-14 flex-shrink-0 flex justify-center">
          {line.damageManaged && !computed ? (
            <Input
              type="number"
              step="1"
              min="1"
              max={MAX_PART_QUANTITY}
              value={lineQuantity(line)}
              onChange={(e) => editLine(line, category, 'quantity', e.target.value)}
              title={`How many of this the repair disturbs (1-${MAX_PART_QUANTITY}). The guide has one line for all of them; hours and price below are for the whole count.`}
              className="w-14 text-center"
            />
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </div>
        {category.hasHours && (
          <Input
            type="number"
            step="0.25"
            min="0"
            placeholder="0"
            value={numericFieldValue(line.hours)}
            onChange={(e) => handleNumericChange(e, (v) => editLine(line, category, 'hours', v))}
            readOnly={computed}
            className="w-16 text-center flex-shrink-0"
          />
        )}
        {/* Amount — dollar-prefixed. Parts price off cost + markup instead. */}
        {isPartsLine ? renderPartsCells(line, category, computed, canOverrideAllowance) : (
          <div className="relative w-24 flex-shrink-0">
            <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
            <Input
              type="number"
              min="0"
              placeholder="0.00"
              value={numericFieldValue(line.amount)}
              onChange={(e) => handleNumericChange(e, (v) => editLine(line, category, 'amount', v))}
              readOnly={amountLocked}
              tabIndex={amountLocked ? -1 : undefined}
              title={
                computed
                  ? allowanceTitle(canOverrideAllowance)
                  : amountLocked
                    ? `Calculated as hours × $${labourRate}/hr. This insurer does not allow editing quote line item prices.`
                    : undefined
              }
              className={`w-full pl-5 text-right ${amountLocked ? 'bg-muted text-muted-foreground cursor-not-allowed' : ''}`}
            />
          </div>
        )}
        {/* Delete — right side, trash icon, revealed on hover (matches damage list) */}
        {computed ? (
          <span className="w-8 flex-shrink-0" />
        ) : (
          <button
            onClick={() => deleteLine(line)}
            className="h-8 w-8 p-0 inline-flex items-center justify-center rounded-md text-slate-400 hover:text-red-600 flex-shrink-0 opacity-0 group-hover/item:opacity-100 transition-opacity"
            title="Remove line"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6">
      {/* Notes */}
      <div className="space-y-2">
        <Label htmlFor="quote-notes">Notes</Label>
        <textarea
          id="quote-notes"
          rows={3}
          placeholder="Additional notes for this quote..."
          value={notes}
          onChange={(e) => updateNotes(e.target.value)}
          className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
      </div>

      {/* Line items by category */}
      <Card>
        <CardHeader>
          <CardTitle>Line Items</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {QUOTE_CATEGORIES.map((category) => {
              const IconComponent = category.icon;
              const isExpanded = expanded[category.id];
              const catLines = linesByCategory[category.id] || [];
              return (
                <div key={category.id} className="space-y-4">
                  <div
                    className="flex items-center gap-2 cursor-pointer hover:bg-muted/30 rounded-md p-2 -m-2 transition-colors"
                    onClick={() => toggleCategory(category.id)}
                  >
                    <div className="flex-1 min-w-0 flex items-center gap-3">
                      <CollapseExpandButton
                        isExpanded={isExpanded}
                        onClick={(e) => { e.stopPropagation(); toggleCategory(category.id); }}
                      />
                      <div className={`flex items-center justify-center w-8 h-8 rounded-md text-foreground ${category.iconClass}`}>
                        <IconComponent className="w-4 h-4" />
                      </div>
                      <span className="font-medium">{category.name}</span>
                      {catLines.length > 0 && (
                        <span className="text-xs text-muted-foreground">({catLines.length})</span>
                      )}
                    </div>
                    {/* Column headers — widths/gaps mirror the line rows so each label
                        sits centred over its field (trailing spacer = delete column) */}
                    {isExpanded ? (
                      <>
                        <span className="w-28 flex-shrink-0 text-center text-sm font-medium text-muted-foreground">Section</span>
                        <span className="w-12 flex-shrink-0 text-center text-sm font-medium text-muted-foreground">Report</span>
                        {category.id === 'Painting' && <span className="w-12 flex-shrink-0 text-center text-sm font-medium text-muted-foreground">Blend</span>}
                        <span className="w-14 flex-shrink-0 text-center text-sm font-medium text-muted-foreground" title="How many of the fitting this line covers">Qty</span>
                        {category.hasHours && <span className="w-16 flex-shrink-0 text-center text-sm font-medium text-muted-foreground">Hours</span>}
                        {category.id === 'Parts' ? (
                          <>
                            {showTypeColumn && <span className="w-28 flex-shrink-0 text-center text-sm font-medium text-muted-foreground">Type</span>}
                            <span className="w-24 flex-shrink-0 text-center text-sm font-medium text-muted-foreground">Cost</span>
                            <span className="w-16 flex-shrink-0 text-center text-sm font-medium text-muted-foreground">Markup</span>
                            <span className="w-24 flex-shrink-0 flex items-center justify-center gap-1 text-sm font-medium text-muted-foreground">
                              Price
                              <Lock className="w-3 h-3 text-muted-foreground" aria-label="Set by cost + markup" />
                            </span>
                          </>
                        ) : (
                          <span className="w-24 flex-shrink-0 flex items-center justify-center gap-1 text-sm font-medium text-muted-foreground">
                            $
                            {category.hasHours && canDerive && !canUpdateLinePrice && (
                              <Lock className="w-3 h-3 text-muted-foreground" aria-label="Set by hours × rate" />
                            )}
                          </span>
                        )}
                        <span className="w-8 flex-shrink-0" />
                      </>
                    ) : (
                      <span className="text-sm font-semibold flex-shrink-0">${(categoryTotals[category.id] || 0).toFixed(2)}</span>
                    )}
                  </div>

                  {isExpanded && (
                    <div className="space-y-2">
                      {catLines.map((line) => renderLineRow(line, category))}

                      {/* Add line row. Damage categories create a damage part (shown
                          in its default section, editable above); the rest add a
                          quote-only line. pl aligns it with the line rows above. */}
                      <div className="flex items-center gap-2 pt-1 pl-7.5">
                        <div className="flex-1">
                          <PartLookupInput
                            value=""
                            onChange={(name) => { if (name && name.trim()) submitAdd(category, name); }}
                            onPartSelected={(sel) => { if (sel && sel.$id) pendingLineRef.current[category.id] = sel.$id; }}
                            insurerConfigId={insurerConfigId}
                            placeholder={`Add a ${category.name.toLowerCase()} line… (Enter to add)`}
                            commitOnBlur={false}
                            clearOnChange
                            className="mb-0"
                          />
                        </div>
                      </div>

                      <div className="flex justify-end pt-2 border-t">
                        <span className="text-sm">
                          <span className="font-medium">{category.name} Total:</span>
                          <span className="ml-2 font-semibold">${(categoryTotals[category.id] || 0).toFixed(2)}</span>
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Quote total */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex justify-end">
            <div className="space-y-1 text-right">
              <div>
                <span className="text-sm text-muted-foreground">Subtotal:</span>
                <span className="ml-3 text-sm font-medium">${totals.subtotal.toFixed(2)}</span>
              </div>
              <div>
                <span className="text-sm text-muted-foreground">GST (15%):</span>
                <span className="ml-3 text-sm font-medium">${totals.gst.toFixed(2)}</span>
              </div>
              <div className="pt-1 border-t">
                <span className="text-lg font-medium">Total:</span>
                <span className="ml-3 text-2xl font-bold text-primary">${totals.total.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default QuoteEditor;
